import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUploadCloud, FiImage, FiAlertTriangle, FiCheckCircle, FiX, FiRefreshCw, FiZap } = FiIcons;

const CoverUpload = ({ manuscriptId, existingCoverPath, onCoverUpdated }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading]   = useState(false);
  const [warning, setWarning]       = useState('');
  const [success, setSuccess]       = useState('');
  const [error, setError]           = useState('');
  const [compressionInfo, setCompressionInfo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    existingCoverPath
      ? `https://format.gospelwiseauthor.app/${existingCoverPath}`
      : null
  );

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png'].includes(ext)) {
      setError('Only .jpg, .jpeg, and .png files are accepted.');
      return;
    }

    // Local preview
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    setWarning('');
    setSuccess('');
    setError('');
    setCompressionInfo(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('cover', file);
      formData.append('manuscript_id', manuscriptId);

      const res  = await fetch('/api/upload-cover.php', { method: 'POST', body: formData });
      const data = await res.json();

      if (!data.success) {
        setError(data.error || 'Upload failed.');
        setPreviewUrl(existingCoverPath
          ? `https://format.gospelwiseauthor.app/${existingCoverPath}`
          : null
        );
        return;
      }

      if (data.warning) setWarning(data.warning);

      if (data.compressed && data.saved_percent > 0) {
        setCompressionInfo(
          `Compressed from ${data.original_size_kb}KB → ${data.saved_size_kb}KB (${data.saved_percent}% smaller)`
        );
      }

      setSuccess(`Cover uploaded — ${data.width}×${data.height}px.`);

      // Update preview to the server-stored version
      setPreviewUrl(`https://format.gospelwiseauthor.app/${data.cover_path}?t=${Date.now()}`);

      if (onCoverUpdated) onCoverUpdated(data.cover_path);
    } catch (err) {
      setError('Network error during upload.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center space-x-3">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
          <SafeIcon icon={FiImage} className="text-primary text-lg" />
        </div>
        <div>
          <h3 className="font-bold text-slate-800">Cover Image</h3>
          <p className="text-xs text-slate-500">
            .jpg or .png · Auto-compressed on upload · Minimum 2,560px tall recommended
          </p>
        </div>
      </div>

      <div className="p-6 flex flex-col sm:flex-row gap-6 items-start">
        {/* Preview */}
        <div
          className="flex-shrink-0 w-32 h-44 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden cursor-pointer hover:border-primary transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Cover preview"
              className="w-full h-full object-cover rounded-xl"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="text-center text-slate-400 p-2">
              <SafeIcon icon={FiImage} className="text-3xl mx-auto mb-1 opacity-40" />
              <span className="text-xs font-bold">No cover</span>
            </div>
          )}
        </div>

        {/* Upload controls */}
        <div className="flex-1">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center space-x-2 bg-primary hover:bg-blue-800 disabled:opacity-50 text-white px-5 py-3 rounded-xl font-bold text-sm uppercase tracking-widest transition-all mb-4"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Uploading &amp; Compressing...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={previewUrl ? FiRefreshCw : FiUploadCloud} />
                <span>{previewUrl ? 'Replace Cover' : 'Upload Cover'}</span>
              </>
            )}
          </button>

          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Accepted: <strong>JPG, JPEG, PNG</strong>.<br />
            Images are automatically compressed (max 1600px, 85% quality) to keep
            your EPUB file size small. Retailers require covers at least <strong>2,560px tall</strong>.
          </p>

          {/* Status messages */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex items-start space-x-2 bg-red-50 border border-red-100 rounded-xl p-3 mb-2"
              >
                <SafeIcon icon={FiX} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </motion.div>
            )}
            {warning && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex items-start space-x-2 bg-amber-50 border border-amber-100 rounded-xl p-3 mb-2"
              >
                <SafeIcon icon={FiAlertTriangle} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900 font-medium">{warning}</p>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex items-center space-x-2 bg-green-50 border border-green-100 rounded-xl p-3 mb-2"
              >
                <SafeIcon icon={FiCheckCircle} className="text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-800 font-medium">{success}</p>
              </motion.div>
            )}
            {compressionInfo && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex items-center space-x-2 bg-blue-50 border border-blue-100 rounded-xl p-3"
              >
                <SafeIcon icon={FiZap} className="text-blue-500 flex-shrink-0" />
                <p className="text-sm text-blue-800 font-medium">{compressionInfo}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CoverUpload;